import {
    Badge,
    Box,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/check-transparent.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import { ReactComponent as NotificationsIcon } from '../../../../assets/icons/notification.svg';
import { ReactComponent as OpenInNewIcon } from '../../../../assets/icons/open-new.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';
import {
    actionsContainerStyles,
    cardBodyStyles,
    cardContentStyles,
    cardHeaderStyles,
    checkboxContainerStyles,
    checkboxIconStyles,
    checkboxLabelStyles,
    checkboxStyles,
    checkedIconStyles,
    closeButtonStyles,
    dialogContentStyles,
    dialogPaperStyles,
    dialogTitleStyles,
    discussionCardStyles,
    discussionsContainerStyles,
    getBadgeStyles,
    modalColors,
    notificationButtonStyles,
    openButtonStyles,
    searchBoxStyles,
    searchInputStyles,
    tabsContainerStyles,
    tabsStyles,
    timestampStyles,
    workTitleStyles,
    workTypeStyles
} from './styled';
import { useAppDispatch } from '../../../../hooks';
import {
    useConversations,
    ChatSelectors
} from '../../../../redux/chat/selectors';
import {
    openChat,
    setActiveConversation,
    setActiveDiscussionsTab,
    markAllMessagesRead
} from '../../../../redux/chat/slice';
import { fetchUserConversationState } from '../../../../redux/chat/service';
import { CONVERSATION_PAGE_SIZE } from '../../../../constants/chat';
import { parseEntityId } from '../../../../utils/chat';
import { ConversationType } from '../../../../enums/chat';
import { ConversationStateSchema } from '../../../../types/chat';
import { useSelector } from 'react-redux';
import { ClientSelectors } from '../../../../redux/client/selectors';
import { getProject } from '../../../../redux/projects/selectors';
import color from '../../../../theme/color';
import { selectProject } from '../../../../redux/client/service';
import { humanize } from '../../../../utils/string';

interface DiscussionsModalProps {
    open: boolean;
    onClose: () => void;
    onOpenChat?: () => void;
}

type TabDefinition = {
    id: string;
    label: string;
    hasNotification: boolean;
};

const getConversationTypeLabel = (type: ConversationType | null): string => {
    switch (type) {
        case ConversationType.PROJECT:
            return 'Project';
        case ConversationType.PROJECT_TECHNICAL:
            return 'Technical';
        case ConversationType.REQUEST:
            return 'Request';
        case ConversationType.TASK:
            return 'Task';
        case ConversationType.WORK:
            return 'Work';
        default:
            return 'Conversation';
    }
};

const getLatestTimestamp = (conversation: ConversationStateSchema): number => {
    if (!conversation.messages || conversation.messages.length === 0) {
        return 0;
    }
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    return lastMessage?.timestampEpochMs ?? 0;
};

const getEntityPageUrl = (
    conversation: ConversationStateSchema
): string | null => {
    if (!conversation.entityId) {
        return null;
    }
    const { conversationType, entityRefId } = parseEntityId(
        conversation.entityId
    );
    switch (conversationType) {
        case ConversationType.PROJECT:
            return '/projects';
        case ConversationType.REQUEST:
            return '/quest/' + entityRefId;
        default:
            return null;
    }
};

export default function DiscussionsModal({
    open,
    onClose,
    onOpenChat
}: DiscussionsModalProps): JSX.Element {
    const dispatch = useAppDispatch();
    const conversationsMap = useConversations();
    const { selectedProjectId } = ClientSelectors();
    const selectedProject = useSelector(getProject(selectedProjectId));
    const { activeDiscussionsTabId } = ChatSelectors();

    const conversations = useMemo(
        () =>
            Object.values(conversationsMap).filter(
                (conversation): conversation is ConversationStateSchema =>
                    Boolean(conversation?.conversationId)
            ),
        [conversationsMap]
    );

    const groupedConversations = useMemo(() => {
        return conversations.reduce<Record<string, ConversationStateSchema[]>>(
            (groups, conversation) => {
                if (
                    conversation.projectId === undefined ||
                    conversation.projectId === null
                ) {
                    return groups;
                }
                const key = String(conversation.projectId);
                if (!groups[key]) {
                    groups[key] = [];
                }
                groups[key].push(conversation);
                return groups;
            },
            {} as Record<string, ConversationStateSchema[]>
        );
    }, [conversations]);

    const [searchQuery, setSearchQuery] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(true);

    const tabs = useMemo<TabDefinition[]>(() => {
        const normalizedSearch = searchQuery.trim().toLowerCase();

        const projectTabs = Object.entries(groupedConversations).reduce(
            (tabsAcc, [id, grouped]) => {
                const matchesSearch = normalizedSearch
                    ? grouped.some((conversation) =>
                          conversation.conversationName
                              .toLowerCase()
                              .includes(normalizedSearch)
                      )
                    : true;

                if (!matchesSearch) {
                    return tabsAcc;
                }

                const firstConversation = grouped[0];
                let label =
                    firstConversation?.projectName ??
                    firstConversation?.conversationName ??
                    `Project ${id}`;

                if (
                    selectedProjectId &&
                    id === String(selectedProjectId) &&
                    selectedProject?.projectName
                ) {
                    label = selectedProject.projectName;
                }

                const hasUnreadConversation = grouped.some(
                    (conversation) => conversation.unreadMessageCount > 0
                );

                if (unreadOnly && !hasUnreadConversation) {
                    return tabsAcc;
                }

                const latestTimestamp = grouped.reduce(
                    (maxTimestamp, conversation) =>
                        Math.max(
                            maxTimestamp,
                            getLatestTimestamp(conversation)
                        ),
                    0
                );

                tabsAcc.push({
                    id,
                    label,
                    hasNotification: hasUnreadConversation,
                    latestTimestamp
                });

                return tabsAcc;
            },
            [] as Array<
                TabDefinition & {
                    latestTimestamp: number;
                }
            >
        );

        const selectedId = selectedProjectId ? String(selectedProjectId) : null;

        const selectedTab = selectedId
            ? projectTabs.find((tab) => tab.id === selectedId)
            : undefined;

        const remainingTabs = projectTabs.filter(
            (tab) => !selectedTab || tab.id !== selectedTab.id
        );

        const unreadTabs = remainingTabs
            .filter((tab) => tab.hasNotification)
            .sort(
                (a, b) => (b.latestTimestamp ?? 0) - (a.latestTimestamp ?? 0)
            );

        const readTabs = remainingTabs
            .filter((tab) => !tab.hasNotification)
            .sort(
                (a, b) => (b.latestTimestamp ?? 0) - (a.latestTimestamp ?? 0)
            );

        const orderedTabs = [
            ...(selectedTab ? [selectedTab] : []),
            ...unreadTabs,
            ...readTabs
        ];

        return orderedTabs.map(({ latestTimestamp: _unused, ...rest }) => rest);
    }, [
        groupedConversations,
        searchQuery,
        selectedProjectId,
        selectedProject,
        unreadOnly
    ]);

    // Compute activeTab index from activeDiscussionsTabId (project ID)
    const activeTab = useMemo(() => {
        if (!activeDiscussionsTabId) {
            // If no saved tab ID, default to selected project tab or first tab
            const selectedId = selectedProjectId
                ? String(selectedProjectId)
                : null;
            if (selectedId) {
                const selectedTabIndex = tabs.findIndex(
                    (tab) => tab.id === selectedId
                );
                return selectedTabIndex >= 0 ? selectedTabIndex : 0;
            }
            return 0;
        }
        const tabIndex = tabs.findIndex(
            (tab) => tab.id === activeDiscussionsTabId
        );
        return tabIndex >= 0 ? tabIndex : 0;
    }, [activeDiscussionsTabId, tabs, selectedProjectId]);

    const filteredConversations = useMemo(() => {
        const currentTab = tabs[activeTab];
        if (!currentTab) {
            return [] as ConversationStateSchema[];
        }

        const rawList = groupedConversations[currentTab.id] ?? [];

        let list = [...rawList];

        if (searchQuery.trim()) {
            setUnreadOnly(false);
            const query = searchQuery.trim().toLowerCase();
            list = list.filter((conversation) =>
                conversation.conversationName.toLowerCase().includes(query)
            );
        }

        if (unreadOnly) {
            list = list.filter(
                (conversation) => conversation.unreadMessageCount > 0
            );
        }

        const getTypePriority = (type: ConversationType | null): number => {
            if (type === ConversationType.PROJECT) return 1;
            if (type === ConversationType.PROJECT_TECHNICAL) return 2;
            return 3;
        };

        return list.sort((a, b) => {
            const aType = parseEntityId(a.entityId).conversationType;
            const bType = parseEntityId(b.entityId).conversationType;
            const aPriority = getTypePriority(aType);
            const bPriority = getTypePriority(bType);

            // Project and project-technical always come before other types
            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // Within the same type tier, prioritize unread conversations
            const aUnread = a.unreadMessageCount > 0;
            const bUnread = b.unreadMessageCount > 0;

            if (aUnread && !bUnread) return -1;
            if (!aUnread && bUnread) return 1;

            // Within the same type and read/unread group, sort by timestamp descending
            const aTimestamp = getLatestTimestamp(a);
            const bTimestamp = getLatestTimestamp(b);

            if (aTimestamp === 0 && bTimestamp === 0) return 0;
            if (aTimestamp === 0) return 1;
            if (bTimestamp === 0) return -1;

            return bTimestamp - aTimestamp;
        });
    }, [activeTab, groupedConversations, searchQuery, tabs, unreadOnly]);

    const loading = conversations.some(
        (conversation) => conversation.isLoading
    );

    const errorMessage =
        conversations.find((conversation) => conversation.conversationError)
            ?.conversationError ?? null;

    const formatTimestamp = (timestampEpochMs: number) => {
        if (!timestampEpochMs) {
            return '';
        }

        const date = new Date(timestampEpochMs);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'Today';
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const handleTabChange = useCallback(
        (_event: React.SyntheticEvent, newValue: number) => {
            const selectedTab = tabs[newValue];
            if (selectedTab) {
                dispatch(
                    setActiveDiscussionsTab({ projectId: selectedTab.id })
                );
            }
        },
        [tabs, dispatch]
    );

    const handleOpenChat = useCallback(
        async (conversation: ConversationStateSchema) => {
            if (!conversation.conversationId) {
                return;
            }

            dispatch(setActiveConversation(conversation.conversationId));
            dispatch(
                setActiveDiscussionsTab({
                    projectId: String(conversation.projectId)
                })
            );

            // Fetch user conversation state BEFORE opening chat to ensure viewPageNumber is set correctly
            // This prevents race conditions where openChat triggers fetchConversation with stale viewPageNumber
            if (conversation.entityId) {
                try {
                    await dispatch(
                        fetchUserConversationState({
                            entityId: conversation.entityId,
                            resultsPerPage: CONVERSATION_PAGE_SIZE
                        })
                    )
                        .unwrap()
                        .then(() => {
                            dispatch(
                                openChat({
                                    conversationId: conversation.conversationId,
                                    entityId: conversation.entityId,
                                    projectId: conversation.projectId ?? null
                                })
                            );
                        });
                } catch (error) {
                    // ignore user state fetch errors
                }
            }
            onOpenChat?.();

            // Immediately mark messages as read in the UI to update the unread badge
            // This provides instant visual feedback without waiting for the next permissions poll
            dispatch(
                markAllMessagesRead({
                    conversationId: conversation.conversationId
                })
            );
        },
        [dispatch, onOpenChat]
    );

    const handleCloseChat = useCallback(() => {
        setSearchQuery('');
        setUnreadOnly(true);
        onClose();
    }, [onClose]);

    const handleOpenEntityPage = useCallback(
        (conversation: ConversationStateSchema) => {
            const url = getEntityPageUrl(conversation);
            if (!url) {
                return;
            }
            window.open(url, '_blank');
        },
        []
    );

    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            hasAutoOpenedRef.current = false;
            return;
        }

        if (conversations.length !== 1) {
            hasAutoOpenedRef.current = false;
            return;
        }

        const [onlyConversation] = conversations;
        if (!onlyConversation || hasAutoOpenedRef.current) {
            return;
        }

        hasAutoOpenedRef.current = true;
        void handleOpenChat(onlyConversation);
    }, [open, conversations, handleOpenChat]);

    return (
        <Dialog
            open={open}
            onClose={handleCloseChat}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: dialogPaperStyles
            }}
        >
            <DialogTitle sx={dialogTitleStyles}>
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        color: 'white',
                        margin: 0,
                        flex: 1
                    }}
                >
                    Discussions
                </Typography>
                <IconButton onClick={onClose} sx={closeButtonStyles}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={dialogContentStyles}>
                <Box sx={searchBoxStyles}>
                    <TextField
                        fullWidth
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        size="small"
                        InputProps={{
                            endAdornment: <SearchIcon />,
                            sx: searchInputStyles
                        }}
                    />
                </Box>

                <Box sx={checkboxContainerStyles}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={unreadOnly}
                                onChange={(event) =>
                                    setUnreadOnly(event.target.checked)
                                }
                                icon={<Box sx={checkboxIconStyles} />}
                                checkedIcon={
                                    <Box sx={checkedIconStyles}>
                                        <CheckIcon />
                                    </Box>
                                }
                            />
                        }
                        label={
                            <Typography
                                variant="body2"
                                sx={checkboxLabelStyles}
                            >
                                Unread only
                            </Typography>
                        }
                        sx={checkboxStyles}
                    />
                </Box>

                {tabs.length > 1 && (
                    <Box sx={tabsContainerStyles}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={tabsStyles}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={tab.id}
                                    label={
                                        tab.hasNotification ? (
                                            <Badge
                                                variant="dot"
                                                sx={getBadgeStyles(
                                                    activeTab === index
                                                )}
                                            >
                                                {humanize(tab.label)}
                                            </Badge>
                                        ) : (
                                            humanize(tab.label)
                                        )
                                    }
                                    sx={{
                                        color:
                                            activeTab === index
                                                ? modalColors.mainGold
                                                : 'grey.300',
                                        minHeight: 'auto',
                                        py: 1,
                                        px: 2,
                                        textTransform: 'none',
                                        fontSize: '0.875rem'
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                )}
                <Box sx={discussionsContainerStyles}>
                    {loading ? (
                        <Typography
                            variant="body2"
                            color={color.lightGrayishBlue}
                            textAlign="center"
                            py={4}
                        >
                            Loading discussions...
                        </Typography>
                    ) : errorMessage ? (
                        <Typography
                            variant="body2"
                            color="error"
                            textAlign="center"
                            py={4}
                        >
                            Error loading discussions: {errorMessage}
                        </Typography>
                    ) : filteredConversations.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: 4
                            }}
                        >
                            <Typography
                                variant="body2"
                                color={color.lightGrayishBlue}
                                textAlign="center"
                            >
                                {unreadOnly
                                    ? 'No unread discussions'
                                    : 'No discussions found'}
                            </Typography>
                            {unreadOnly && (
                                <Typography
                                    component="a"
                                    variant="body2"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setUnreadOnly(false);
                                    }}
                                    sx={{
                                        color: modalColors.mainGold,
                                        textAlign: 'center',
                                        mt: 1,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                            color: modalColors.mainGold,
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    Show all discussions
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {filteredConversations.map((conversation) => {
                                const { conversationType } = parseEntityId(
                                    conversation.entityId
                                );
                                const conversationTypeLabel =
                                    getConversationTypeLabel(conversationType);
                                const lastTimestamp =
                                    getLatestTimestamp(conversation);
                                const entityPageUrl =
                                    getEntityPageUrl(conversation);

                                return (
                                    <Card
                                        key={conversation.conversationId}
                                        sx={discussionCardStyles}
                                        onClick={() =>
                                            handleOpenChat(conversation)
                                        }
                                    >
                                        <CardContent sx={cardContentStyles}>
                                            <Box sx={cardHeaderStyles}>
                                                <Box sx={cardBodyStyles}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={workTypeStyles}
                                                    >
                                                        {conversationTypeLabel}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={workTitleStyles}
                                                    >
                                                        {humanize(
                                                            conversation.conversationName
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={timestampStyles}
                                                    >
                                                        {formatTimestamp(
                                                            lastTimestamp
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={actionsContainerStyles}
                                                >
                                                    {conversation.unreadMessageCount >
                                                        0 && (
                                                        <IconButton
                                                            size="small"
                                                            sx={
                                                                notificationButtonStyles
                                                            }
                                                        >
                                                            <NotificationsIcon
                                                                fontSize="small"
                                                                fill={
                                                                    modalColors.mainGold
                                                                }
                                                            />
                                                        </IconButton>
                                                    )}
                                                    {entityPageUrl && (
                                                        <IconButton
                                                            size="small"
                                                            sx={
                                                                openButtonStyles
                                                            }
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                if (
                                                                    conversation.projectId
                                                                ) {
                                                                    console.log(
                                                                        'Selecting project before opening entity page:',
                                                                        conversation.projectId
                                                                    );
                                                                    dispatch(
                                                                        selectProject(
                                                                            String(
                                                                                conversation.projectId
                                                                            )
                                                                        )
                                                                    );
                                                                }
                                                                handleOpenEntityPage(
                                                                    conversation
                                                                );
                                                            }}
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
